import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Avatar,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  LinearProgress,
  Alert,
} from '@mui/material';
import { VisibilityOutlined as Visibility, StarOutlined as Star, ArrowUpwardOutlined as ArrowUpward, ArrowDownwardOutlined as ArrowDownward, ThumbUpOutlined as ThumbUp, ThumbDownOutlined as ThumbDown } from '@mui/icons-material';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { fetchAllChats, fetchChatRatings, ChatSession, MessageRating } from '../../store/slices/adminSlice';
import { format } from 'date-fns';
import ReactMarkdown from 'react-markdown';
import { preprocessMarkdown } from '../../utils/markdownPreprocessor';
import Pagination from '../common/Pagination';

interface ChatMonitorProps {
  users: any[];
}

const ChatMonitor: React.FC<ChatMonitorProps> = ({ users }) => {
  const dispatch = useAppDispatch();
  const { chatSessions, loading, error } = useAppSelector((state: any) => state.admin);
  const [selectedUserId, setSelectedUserId] = useState<number | ''>('');
  const [selectedChat, setSelectedChat] = useState<ChatSession | null>(null);
  const [chatDialogOpen, setChatDialogOpen] = useState(false);
  const [ratingsDialogOpen, setRatingsDialogOpen] = useState(false);
  const [selectedChatRatings, setSelectedChatRatings] = useState<MessageRating[]>([]);
  const [sortField, setSortField] = useState<string>('created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const cardRef = useRef<HTMLDivElement>(null);

  const userById = React.useMemo(() => {
    return users.reduce((acc, user) => {
      acc[user.id] = user;
      return acc;
    }, {} as Record<number, any>);
  }, [users]);

  useEffect(() => {
    dispatch(fetchAllChats());
  }, [dispatch]);

  const handleUserFilter = (userId: number | '') => {
    // Preserve scroll position
    const scrollPosition = window.pageYOffset;
    setSelectedUserId(userId);
    // Restore scroll position after state update
    setTimeout(() => {
      window.scrollTo(0, scrollPosition);
    }, 0);
  };

  const openChatDialog = (chat: ChatSession) => {
    setSelectedChat(chat);
    setChatDialogOpen(true);
  };

  const openRatingsDialog = async (chat: ChatSession) => {
    setSelectedChat(chat);
    try {
      const result = await dispatch(fetchChatRatings(chat.id));
      if (fetchChatRatings.fulfilled.match(result)) {
        setSelectedChatRatings(result.payload);
      }
    } catch (error) {
      console.error('Error fetching ratings:', error);
    }
    setRatingsDialogOpen(true);
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const getSortIcon = (field: string) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />;
  };

  const filteredChats = chatSessions
    .filter((chat: ChatSession) => 
      selectedUserId === '' || chat.user_id === selectedUserId
    )
    .sort((a: ChatSession, b: ChatSession) => {
      let aValue, bValue;
      
      switch (sortField) {
        case 'user':
          aValue = userById[a.user_id]?.username || '';
          bValue = userById[b.user_id]?.username || '';
          break;
        case 'title':
          aValue = a.title || '';
          bValue = b.title || '';
          break;
        case 'messages':
          aValue = a.messages?.length || 0;
          bValue = b.messages?.length || 0;
          break;
        case 'created_at':
          aValue = new Date(a.created_at).getTime();
          bValue = new Date(b.created_at).getTime();
          break;
        default:
          aValue = new Date(a.created_at).getTime();
          bValue = new Date(b.created_at).getTime();
          break;
      }
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const comparison = aValue.localeCompare(bValue);
        return sortDirection === 'asc' ? comparison : -comparison;
      } else {
        const comparison = (aValue as number) - (bValue as number);
        return sortDirection === 'asc' ? comparison : -comparison;
      }
    });

  const paginatedChats = React.useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredChats.slice(startIndex, endIndex);
  }, [filteredChats, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredChats.length / itemsPerPage);

  return (
    <Card ref={cardRef}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 500 }}>
            Chat Sessions Monitor
          </Typography>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Filter by User</InputLabel>
            <Select
              value={selectedUserId}
              label="Filter by User"
              onChange={(e) => handleUserFilter(e.target.value as number | '')}
            >
              <MenuItem value="">All Users</MenuItem>
              {users.map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  {user.username} ({user.email})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <LinearProgress />
        ) : (
          <>
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell 
                      sx={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('user')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        User
                        {getSortIcon('user')}
                      </Box>
                    </TableCell>
                    <TableCell 
                      sx={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('title')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        Session Title
                        {getSortIcon('title')}
                      </Box>
                    </TableCell>
                    <TableCell 
                      sx={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('messages')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        Messages
                        {getSortIcon('messages')}
                      </Box>
                    </TableCell>
                    <TableCell 
                      sx={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('created_at')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        Created
                        {getSortIcon('created_at')}
                      </Box>
                    </TableCell>

                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedChats.map((chat: ChatSession) => (
                    <TableRow key={chat.id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar sx={{ mr: 2, bgcolor: userById[chat.user_id]?.role === 'admin' ? 'primary.main' : 'grey.500' }}>
                            {(userById[chat.user_id]?.username || '?').charAt(0).toUpperCase()}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 400 }}>
                              {userById[chat.user_id]?.username || `User #${chat.user_id}`}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {userById[chat.user_id]?.email || ''}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 300 }} noWrap>
                          {chat.title || `Chat ${chat.id.substring(0, 8)}`}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={`${chat.messages?.length || 0} messages`} 
                          size="small" 
                          color="info"
                        />
                      </TableCell>
                      <TableCell>
                        {format(new Date(chat.created_at), 'dd/MM/yyyy HH:mm')}
                      </TableCell>

                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="View Messages">
                            <IconButton size="small" onClick={() => openChatDialog(chat)}>
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="View Ratings">
                            <IconButton size="small" onClick={() => openRatingsDialog(chat)}>
                              <Star />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                  {paginatedChats.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography color="text.secondary">
                          No chat sessions found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {filteredChats.length > 0 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={filteredChats.length}
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={setItemsPerPage}
              />
            )}
          </>
        )}
      </CardContent>

      {/* Chat Messages Dialog */}
      <Dialog open={chatDialogOpen} onClose={() => setChatDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Chat Session: {selectedChat?.title || `Chat ${selectedChat?.id.substring(0, 8)}`}
        </DialogTitle>
        <DialogContent>
          {selectedChat?.messages && selectedChat.messages.length > 0 ? (
            <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
              {[...selectedChat.messages]
                .sort((a, b) => (a.sequence_number || 0) - (b.sequence_number || 0))
                .map((message, index) => (
                <Box
                  key={message.id || index}
                  sx={{
                    mb: 2,
                    p: 2,
                    borderRadius: 1,
                    bgcolor: message.sender === 'user' ? 'primary.light' : 'grey.100',
                    color: message.sender === 'user' ? 'primary.contrastText' : 'text.primary',
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Chip 
                      label={message.sender === 'user' ? 'User' : 'Chatbot'} 
                      size="small"
                      color={message.sender === 'user' ? 'primary' : 'default'}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {message.created_at ? format(new Date(message.created_at), 'dd/MM, HH:mm') : ''}
                    </Typography>
                  </Box>
                  {message.sender === 'bot' ? (
                    <ReactMarkdown
                      components={{
                        p: ({ children }) => (
                          <Typography variant="body2" sx={{ fontSize: '0.875rem', mb: 1, '&:last-child': { mb: 0 } }}>
                            {children}
                          </Typography>
                        ),
                        h1: ({ children }) => (
                          <Typography variant="h5" sx={{ mb: 1, mt: 1 }}>
                            {children}
                          </Typography>
                        ),
                        h2: ({ children }) => (
                          <Typography variant="h6" sx={{ mb: 1, mt: 1 }}>
                            {children}
                          </Typography>
                        ),
                        h3: ({ children }) => (
                          <Typography variant="subtitle1" sx={{ mb: 0.5, mt: 0.5 }}>
                            {children}
                          </Typography>
                        ),
                        h4: ({ children }) => (
                          <Typography variant="subtitle2" sx={{ mb: 0.5, mt: 0.5 }}>
                            {children}
                          </Typography>
                        ),
                        h5: ({ children }) => (
                          <Typography variant="body1" sx={{ mb: 0.5, mt: 0.5 }}>
                            {children}
                          </Typography>
                        ),
                        h6: ({ children }) => (
                          <Typography variant="body2" sx={{ mb: 0.5, mt: 0.5 }}>
                            {children}
                          </Typography>
                        ),
                        code: (props: any) => {
                          const { inline, children } = props;
                          return inline ? (
                            <Box
                              component="code"
                              sx={{
                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                padding: '2px 4px',
                                borderRadius: '4px',
                                fontFamily: 'monospace',
                                fontSize: '0.875em',
                              }}
                            >
                              {children}
                            </Box>
                          ) : (
                            <Box
                              component="pre"
                              sx={{
                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                padding: '12px',
                                borderRadius: '8px',
                                overflow: 'auto',
                                fontFamily: 'monospace',
                                fontSize: '0.875rem',
                                mb: 1,
                                mt: 1,
                              }}
                            >
                              <code>{children}</code>
                            </Box>
                          );
                        },
                        ul: ({ children }) => (
                          <Box component="ul" sx={{ pl: 2, mb: 1 }}>
                            {children}
                          </Box>
                        ),
                        ol: ({ children }) => (
                          <Box component="ol" sx={{ pl: 2, mb: 1 }}>
                            {children}
                          </Box>
                        ),
                        li: ({ children }) => (
                          <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                            {children}
                          </Typography>
                        ),
                        blockquote: ({ children }) => (
                          <Box
                            sx={{
                              borderLeft: '4px solid #ccc',
                              pl: 2,
                              py: 1,
                              mb: 1,
                              fontStyle: 'italic',
                              backgroundColor: 'rgba(0, 0, 0, 0.02)',
                            }}
                          >
                            {children}
                          </Box>
                        ),
                        strong: ({ children }) => (
                          <Typography component="strong" sx={{ fontWeight: 'bold' }}>
                            {children}
                          </Typography>
                        ),
                        em: ({ children }) => (
                          <Typography component="em" sx={{ fontStyle: 'italic' }}>
                            {children}
                          </Typography>
                        ),
                        table: ({ children }) => (
                          <Box component="table" sx={{ 
                            width: '100%', 
                            borderCollapse: 'collapse', 
                            mb: 2,
                            border: '1px solid #ddd'
                          }}>
                            {children}
                          </Box>
                        ),
                        thead: ({ children }) => (
                          <Box component="thead" sx={{ backgroundColor: '#f5f5f5' }}>
                            {children}
                          </Box>
                        ),
                        tbody: ({ children }) => (
                          <Box component="tbody">
                            {children}
                          </Box>
                        ),
                        tr: ({ children }) => (
                          <Box component="tr" sx={{ borderBottom: '1px solid #ddd' }}>
                            {children}
                          </Box>
                        ),
                        th: ({ children }) => (
                          <Box component="th" sx={{ 
                            p: 1, 
                            textAlign: 'left', 
                            fontWeight: 'bold',
                            border: '1px solid #ddd'
                          }}>
                            {children}
                          </Box>
                        ),
                        td: ({ children }) => (
                          <Box component="td" sx={{ 
                            p: 1, 
                            border: '1px solid #ddd'
                          }}>
                            {children}
                          </Box>
                        ),
                      }}
                    >
                      {preprocessMarkdown(message.content)}
                    </ReactMarkdown>
                  ) : (
                    <Typography variant="body2">
                      {message.content}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          ) : (
            <Typography color="text.secondary">
              No messages in this chat session
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChatDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Ratings Dialog */}
      <Dialog open={ratingsDialogOpen} onClose={() => setRatingsDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Ratings for: {selectedChat?.title || `Chat ${selectedChat?.id.substring(0, 8)}`}
        </DialogTitle>
        <DialogContent>
          {selectedChatRatings.length > 0 ? (
            <Box>
              {selectedChatRatings.map((rating) => (
                <Box key={rating.id} sx={{ mb: 3, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {rating.rating_type === 'thumbs' ? (
                        <Chip 
                          icon={rating.rating_value === 1 ? <ThumbUp /> : <ThumbDown />}
                          label={rating.rating_value === 1 ? 'Thumbs Up' : 'Thumbs Down'}
                          color={rating.rating_value === 1 ? 'success' : 'error'}
                          size="small"
                        />
                      ) : (
                        <>
                          <Star color="warning" />
                          <Typography variant="h6">{rating.rating_value}/5</Typography>
                        </>
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {format(new Date(rating.created_at), 'dd/MM/yyyy HH:mm')}
                    </Typography>
                  </Box>
                  
                  {/* Message Content */}
                  {(rating as any).message_content && (
                    <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Chip 
                          label={(rating as any).message_sender === 'user' ? 'User' : 'Chatbot'} 
                          size="small"
                          color={(rating as any).message_sender === 'user' ? 'primary' : 'default'}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {(rating as any).message_created_at ? format(new Date((rating as any).message_created_at), 'dd/MM, HH:mm') : ''}
                        </Typography>
                      </Box>
                      {(rating as any).message_sender === 'bot' ? (
                        <Box sx={{ fontStyle: 'italic' }}>
                          <ReactMarkdown
                            components={{
                              p: ({ children }) => (
                                <Typography variant="body2" sx={{ fontSize: '0.875rem', mb: 1, '&:last-child': { mb: 0 } }}>
                                  "{children}"
                                </Typography>
                              ),
                              h1: ({ children }) => (
                                <Typography variant="h5" sx={{ mb: 1, mt: 1 }}>
                                  {children}
                                </Typography>
                              ),
                              h2: ({ children }) => (
                                <Typography variant="h6" sx={{ mb: 1, mt: 1 }}>
                                  {children}
                                </Typography>
                              ),
                              h3: ({ children }) => (
                                <Typography variant="subtitle1" sx={{ mb: 0.5, mt: 0.5 }}>
                                  {children}
                                </Typography>
                              ),
                              h4: ({ children }) => (
                                <Typography variant="subtitle2" sx={{ mb: 0.5, mt: 0.5 }}>
                                  {children}
                                </Typography>
                              ),
                              h5: ({ children }) => (
                                <Typography variant="body1" sx={{ mb: 0.5, mt: 0.5 }}>
                                  {children}
                                </Typography>
                              ),
                              h6: ({ children }) => (
                                <Typography variant="body2" sx={{ mb: 0.5, mt: 0.5 }}>
                                  {children}
                                </Typography>
                              ),
                              code: (props: any) => {
                                const { inline, children } = props;
                                return inline ? (
                                  <Box
                                    component="code"
                                    sx={{
                                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                      padding: '2px 4px',
                                      borderRadius: '4px',
                                      fontFamily: 'monospace',
                                      fontSize: '0.875em',
                                    }}
                                  >
                                    {children}
                                  </Box>
                                ) : (
                                  <Box
                                    component="pre"
                                    sx={{
                                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                      padding: '12px',
                                      borderRadius: '8px',
                                      overflow: 'auto',
                                      fontFamily: 'monospace',
                                      fontSize: '0.875rem',
                                      mb: 1,
                                      mt: 1,
                                    }}
                                  >
                                    <code>{children}</code>
                                  </Box>
                                );
                              },
                              ul: ({ children }) => (
                                <Box component="ul" sx={{ pl: 2, mb: 1 }}>
                                  {children}
                                </Box>
                              ),
                              ol: ({ children }) => (
                                <Box component="ol" sx={{ pl: 2, mb: 1 }}>
                                  {children}
                                </Box>
                              ),
                              li: ({ children }) => (
                                <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                                  {children}
                                </Typography>
                              ),
                              blockquote: ({ children }) => (
                                <Box
                                  sx={{
                                    borderLeft: '4px solid #ccc',
                                    pl: 2,
                                    py: 1,
                                    mb: 1,
                                    fontStyle: 'italic',
                                    backgroundColor: 'rgba(0, 0, 0, 0.02)',
                                  }}
                                >
                                  {children}
                                </Box>
                              ),
                              strong: ({ children }) => (
                                <Typography component="strong" sx={{ fontWeight: 'bold' }}>
                                  {children}
                                </Typography>
                              ),
                              em: ({ children }) => (
                                <Typography component="em" sx={{ fontStyle: 'italic' }}>
                                  {children}
                                </Typography>
                              ),
                              table: ({ children }) => (
                                <Box component="table" sx={{ 
                                  width: '100%', 
                                  borderCollapse: 'collapse', 
                                  mb: 2,
                                  border: '1px solid #ddd'
                                }}>
                                  {children}
                                </Box>
                              ),
                              thead: ({ children }) => (
                                <Box component="thead" sx={{ backgroundColor: '#f5f5f5' }}>
                                  {children}
                                </Box>
                              ),
                              tbody: ({ children }) => (
                                <Box component="tbody">
                                  {children}
                                </Box>
                              ),
                              tr: ({ children }) => (
                                <Box component="tr" sx={{ borderBottom: '1px solid #ddd' }}>
                                  {children}
                                </Box>
                              ),
                              th: ({ children }) => (
                                <Box component="th" sx={{ 
                                  p: 1, 
                                  textAlign: 'left', 
                                  fontWeight: 'bold',
                                  border: '1px solid #ddd'
                                }}>
                                  {children}
                                </Box>
                              ),
                              td: ({ children }) => (
                                <Box component="td" sx={{ 
                                  p: 1, 
                                  border: '1px solid #ddd'
                                }}>
                                  {children}
                                </Box>
                              ),
                            }}
                          >
                            {preprocessMarkdown((rating as any).message_content)}
                          </ReactMarkdown>
                        </Box>
                      ) : (
                        <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                          "{(rating as any).message_content}"
                        </Typography>
                      )}
                    </Box>
                  )}
                  
                  {/* Feedback Text */}
                  {rating.feedback_text && (
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 400 }}>
                      Feedback: "{rating.feedback_text}"
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          ) : (
            <Typography color="text.secondary">
              No ratings found for this chat session
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRatingsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default ChatMonitor;